import { Check, ChevronDown, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { RowAction } from "@/components/row-action";
import { Button } from "@/components/ui/button";
import {
  type RoadmapBlocker,
  type RoadmapItem,
  useDeleteRoadmapBlocker,
  useResolveRoadmapBlocker,
} from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { belgianDay, formatDate, formatDayMonth } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { byEmail, firstNameLabel, type NamedPerson, normalEmail } from "@/lib/people";
import { cn } from "@/lib/utils";

import { AddBlockerForm } from "./blocker-form";
import { BLOCKAGE_WRAP, BlockageSymbol, BlockageText } from "./blockers";
import {
  DetailsCard,
  type DetailsContext,
  EditPencil,
  Failure,
  focusFell,
  useFocusBack,
} from "./details-parts";
import { type BlockageLine, blockageLine, isOpenBlocker } from "./utils";

interface BlockersCardProps {
  item: RoadmapItem;
  overview: DetailsContext;
  pastMonth: boolean;
  /** The pencil's mode: every row shows Verwijderen. */
  editing: boolean;
  onEditing: (on: boolean) => void;
  onOpenItem: (id: string) => void;
}

/** A blockage that is changing now: being resolved or removed. */
interface Running {
  id: string;
  action: "resolve" | "remove";
}

/**
 * What the item waits on, one row each: the blockage symbol, "Senne: FinVent
 * openzetten in Odoo", since when, and Opgelost for whoever may resolve it
 * (`can_resolve`). The pencil shows Verwijderen on every row, for one added by
 * mistake (`edit_blockers`). "+ Blokkade" adds one in place. The resolved rows
 * fold away under "2 opgelost". A done item waits on nothing, so it has no
 * card; nothing changes from a past month.
 */
export function BlockersCard({
  item,
  overview,
  pastMonth,
  editing,
  onEditing,
  onOpenItem,
}: BlockersCardProps) {
  const { t } = useTranslation();
  const resolve = useResolveRoadmapBlocker();
  const remove = useDeleteRoadmapBlocker();
  const pencil = useFocusBack(editing);
  const [running, setRunning] = useState<Running | null>(null);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{
    error: unknown;
    action: "resolveRoadmapBlocker" | "deleteRoadmapBlocker";
  } | null>(null);
  const [adding, setAdding] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  // Set when the form closes, so the focus goes back to "+ Blokkade" once it shows again.
  const returnToAdd = useRef(false);

  useEffect(() => {
    if (!adding && returnToAdd.current) {
      returnToAdd.current = false;
      addButton.current?.focus();
    }
  }, [adding]);

  const context = {
    people: byEmail(overview.people),
    items: new Map(overview.items.map((other) => [other.id, other])),
    viewer: overview.viewer.email,
  };
  const rows = item.blockers.flatMap((blocker) => {
    const line = blockageLine(blocker, context);
    return line ? [{ blocker, line }] : [];
  });
  const open = rows.filter((row) => isOpenBlocker(row.blocker));
  const resolved = rows.filter((row) => !isOpenBlocker(row.blocker));
  const canEdit = !pastMonth && item.can.edit_blockers;
  const none = rows.length === 0;
  const hadRows = useRef(!none);

  // The last blockage went: Verwijderen has nothing left to remove, so its mode ends (its
  // pencil goes too), and a focus that fell with the list goes to "+ Blokkade".
  useEffect(() => {
    const emptied = hadRows.current && none;
    hadRows.current = !none;
    if (!emptied) {
      return;
    }
    if (editing) {
      onEditing(false);
    }
    if (focusFell()) {
      addButton.current?.focus();
    }
  }, [none, editing, onEditing]);

  const run = async (blocker: RoadmapBlocker, action: Running["action"]) => {
    if (running) {
      return;
    }
    setFailure(null);
    setRunning({ id: blocker.id, action });
    try {
      const vars = { itemId: item.id, blockerId: blocker.id };
      await (action === "resolve" ? resolve.mutateAsync(vars) : remove.mutateAsync(vars));
      // The row moves or leaves: the focus goes to the list, never to the page's body.
      list.current?.focus();
    } catch (error) {
      setFailure(
        inlineFailure({
          error,
          action: action === "resolve" ? "resolveRoadmapBlocker" : "deleteRoadmapBlocker",
        }),
      );
    } finally {
      setRunning(null);
    }
  };

  const removeButton = (blocker: RoadmapBlocker, line: BlockageLine) =>
    editing &&
    canEdit && (
      <RowAction
        label={t("roadmap.blockers.remove", { who: line.who })}
        icon={running?.id === blocker.id && running.action === "remove" ? Loader2 : Trash2}
        destructive
        onClick={() => void run(blocker, "remove")}
      />
    );

  // A done item waits on nothing: its blockages no longer matter, as in the table.
  if (item.status === "done") {
    return null;
  }

  // No blockage yet: the card is its header alone, with "+ Blokkade" in it.
  const empty = rows.length === 0 && !adding;
  const addControl = (inHeader: boolean) => (
    <Button
      ref={addButton}
      variant="ghost"
      size="sm"
      className={cn("text-primary h-8 px-2 pointer-coarse:h-10", !inHeader && "mt-2 -ml-2")}
      onClick={() => {
        setFailure(null);
        setAdding(true);
      }}
      aria-label={t("roadmap.blockers.addTitle")}
      data-blocker-action="add"
    >
      <Plus aria-hidden="true" className="size-4" />
      {t("roadmap.blockers.add")}
    </Button>
  );

  return (
    <DetailsCard
      title={t("roadmap.blockers.card")}
      note={
        empty
          ? t("roadmap.blockers.cardNone")
          : t("roadmap.blockers.cardOpen", { count: open.length })
      }
      section="blockers"
      aside={empty && canEdit ? addControl(true) : undefined}
      pencil={
        canEdit &&
        rows.length > 0 && (
          <EditPencil
            ref={pencil}
            label={t(editing ? "roadmap.blockers.removeDone" : "roadmap.blockers.removeMode")}
            pressed={editing}
            onClick={() => onEditing(!editing)}
          />
        )
      }
    >
      {!empty && (
        <>
          <div ref={list} tabIndex={-1} className="focus:outline-none">
            {open.length === 0 && !adding && (
              <p className="text-muted text-sm">{t("roadmap.blockers.nothing")}</p>
            )}
            {open.length > 0 && (
              <ul className="divide-border divide-y text-sm">
                {open.map(({ blocker, line }) => {
                  const busy = running?.id === blocker.id && running.action === "resolve";
                  return (
                    <li
                      key={blocker.id}
                      // The actions go under the words once both no longer fit on one line (a phone).
                      className="flex flex-wrap items-start gap-x-2 gap-y-1 py-1.5"
                      data-blocker="open"
                      data-mine={line.mine || undefined}
                    >
                      <p
                        className={cn(
                          "flex min-w-48 flex-1 gap-2 pt-1",
                          BLOCKAGE_WRAP,
                          line.mine ? "text-primary" : "text-foreground",
                        )}
                      >
                        <BlockageSymbol className="mt-0.5 size-4" />
                        <span className="min-w-0">
                          <BlockageText
                            line={line}
                            language={item.language}
                            onOpenItem={onOpenItem}
                          />
                          <span className="text-muted ml-2 text-xs whitespace-nowrap">
                            {t("roadmap.blockers.since", { date: shortDay(blocker.created_at) })}
                          </span>
                        </span>
                      </p>
                      <div className="ml-auto flex shrink-0 items-center gap-1">
                        {!pastMonth && blocker.can_resolve && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs pointer-coarse:h-10"
                            aria-label={t("roadmap.blockers.resolveLabel", { who: line.who })}
                            // Never `disabled`: that would drop the focus while it runs.
                            aria-disabled={running !== null || undefined}
                            onClick={() => void run(blocker, "resolve")}
                            data-blocker-action="resolve"
                          >
                            {busy ? (
                              <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                            ) : (
                              <Check aria-hidden="true" className="size-3.5" />
                            )}
                            {t("roadmap.blockers.resolve")}
                          </Button>
                        )}
                        {removeButton(blocker, line)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {canEdit &&
            (adding ? (
              <div className="mt-3">
                <AddBlockerForm
                  item={item}
                  overview={overview}
                  onClose={() => {
                    returnToAdd.current = true;
                    setAdding(false);
                  }}
                />
              </div>
            ) : (
              addControl(false)
            ))}
          {failure && (
            <div className="mt-2">
              <Failure>{apiErrorMessage(failure.error, failure.action)}</Failure>
            </div>
          )}
          {resolved.length > 0 && (
            <div className="mt-2 border-t pt-2">
              <button
                type="button"
                aria-expanded={showResolved}
                onClick={() => setShowResolved(!showResolved)}
                className="text-muted hover:text-primary focus-visible:ring-ring tap-target inline-flex items-center gap-1 rounded-sm text-xs font-semibold focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
                data-blocker-action="show-resolved"
              >
                {showResolved ? (
                  <ChevronDown aria-hidden="true" className="size-3.5" />
                ) : (
                  <ChevronRight aria-hidden="true" className="size-3.5" />
                )}
                {t("roadmap.blockers.resolvedCount", { count: resolved.length })}
              </button>
              {showResolved && (
                <ul className="text-muted mt-1 text-sm">
                  {resolved.map(({ blocker, line }) => (
                    <li
                      key={blocker.id}
                      className="flex items-start gap-2 py-0.5"
                      data-blocker="resolved"
                    >
                      <p className={cn("flex min-w-0 flex-1 gap-2 pt-1.5", BLOCKAGE_WRAP)}>
                        <BlockageSymbol className="mt-0.5 size-4" />
                        <span className="min-w-0">
                          <span className="line-through">
                            <BlockageText
                              line={line}
                              language={item.language}
                              onOpenItem={onOpenItem}
                            />
                          </span>
                          <span className="ml-2 text-xs">
                            {resolvedNote(blocker, context.people)}
                          </span>
                        </span>
                      </p>
                      <div className="flex shrink-0 items-center">
                        {removeButton(blocker, line)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </DetailsCard>
  );
}

/** A moment's day as "30/10", in Belgium. */
function shortDay(at: string): string {
  const day = belgianDay(at);
  return day ? formatDayMonth(day) : formatDate(at);
}

/**
 * Who resolved a blockage, by first name (`firstNameLabel`), and when:
 * "opgelost door Ruben op 30/10", or "opgelost op 05/11" for one closed by
 * the item it waits on being done.
 */
function resolvedNote(blocker: RoadmapBlocker, people: ReadonlyMap<string, NamedPerson>): string {
  const date = blocker.resolved_at ? shortDay(blocker.resolved_at) : "";
  if (!blocker.resolved_by) {
    return i18n.t("roadmap.blockers.resolvedOn", { date });
  }
  const person = people.get(normalEmail(blocker.resolved_by)) ?? { email: blocker.resolved_by };
  const name = firstNameLabel(people.values());
  return i18n.t("roadmap.blockers.resolvedBy", { person: name(person), date });
}
